import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bot, Briefcase, Check, FileSpreadsheet, LineChart, MessageCircle, ShieldCheck, Target, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/kit";
import { Testimonials } from "@/components/Testimonials";
import { api, brl } from "@/lib/api";

const FEATURES = [
  [LineChart, "Diagnóstico inteligente", "Saldo, patrimônio líquido, reserva, score de saúde financeira e prioridades em segundos."],
  [Target, "Planos 12 meses, 5 e 30 anos", "Estratégia por horizonte com cronograma mensal de tarefas e metas acompanháveis."],
  [Briefcase, "Gestão empresarial", "Fluxo de caixa, margem de contribuição, ponto de equilíbrio e capital de giro."],
  [Bot, "Mentor financeiro IA", "Orientação personalizada com base nos seus números, sem promessas de retorno."],
  [FileSpreadsheet, "Relatórios PDF e Excel", "Relatórios executivos e planilhas completas para você ou seu contador."],
  [Users, "Família e equipes", "Espaços compartilhados com permissões de editor ou leitura."],
];

function Plans() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
      {plans.map((p) => (
        <div key={p.key} className={`card-p flex flex-col p-6 ${p.key === "premium" ? "shadow-[0_0_40px_rgba(212,175,55,0.15)] !border-[#D4AF37]/60" : ""}`} data-testid={`landing-plan-${p.key}`}>
          <div className="text-sm font-semibold uppercase tracking-widest text-[#D4AF37]">{p.name}</div>
          <div className="num mt-3 text-3xl font-semibold">{brl(p.price)}<span className="text-sm font-normal text-slate-400">/mês</span></div>
          <ul className="mt-5 flex-1 space-y-2 text-sm text-slate-300">
            {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />{f}</li>)}
          </ul>
          <Link to="/cadastro" className="mt-6"><Button className="w-full" variant={p.key === "premium" ? "default" : "outline"} data-testid={`landing-plan-cta-${p.key}`}>Começar teste grátis</Button></Link>
        </div>
      ))}
    </div>
  );
}

export default function Landing() {
  return (
    <div className="grain min-h-screen bg-[#060D1A]">
      <header className="glass sticky top-0 z-30 border-b border-[#1E3A6E]/60">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <img src="/assets/prospera-icon.png" alt="PROSPERA+" className="h-9 w-9 rounded-lg" data-testid="landing-icon" />
            <span className="font-display text-lg font-bold tracking-wide">PROSPERA<span className="text-[#D4AF37]">+</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" data-testid="landing-login-btn">Entrar</Button></Link>
            <Link to="/cadastro"><Button data-testid="landing-register-btn">Criar conta</Button></Link>
          </div>
        </div>
      </header>
      <section className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 py-16 md:py-24 lg:grid-cols-12">
        <div className="lg:col-span-7 fade-up">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-xs text-[#F5D77F]">
            <ShieldCheck className="h-3.5 w-3.5" /> Dados isolados e em conformidade com a LGPD
          </div>
          <h1 className="text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">Organize o presente.<br />Planeje o futuro.<br /><span className="gold-text">Construa seu patrimônio.</span></h1>
          <p className="mt-6 max-w-xl text-base text-slate-400 md:text-lg">Inteligência financeira e patrimonial para pessoas, famílias, autônomos e empresas — com diagnóstico, estratégias de curto a longo prazo e um mentor com IA.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/cadastro"><Button size="lg" data-testid="hero-cta">Começar 14 dias grátis <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
            <Link to="/login"><Button size="lg" variant="outline" className="border-[#1E3A6E] bg-transparent" data-testid="hero-demo">Acessar demonstração</Button></Link>
          </div>
        </div>
        <div className="lg:col-span-5 fade-up" style={{ animationDelay: "150ms" }}>
          <Logo className="mx-auto w-full max-w-md rounded-3xl shadow-[0_30px_90px_rgba(0,0,0,.6)] ring-1 ring-[#D4AF37]/25" testid="hero-logo" />
        </div>
      </section>
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-16">
        <h2 className="mb-10 text-2xl font-semibold sm:text-3xl">Tudo o que você precisa para prosperar com método</h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(([I, t, d], i) => (
            <div key={t} className="card-p p-6 fade-up hover:-translate-y-1" style={{ animationDelay: `${i * 60}ms` }}>
              <I className="h-6 w-6 text-[#D4AF37]" />
              <h3 className="mt-4 text-lg font-semibold">{t}</h3>
              <p className="mt-2 text-sm text-slate-400">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <Testimonials />
      <section className="relative z-10 mx-auto max-w-7xl px-5 py-16" id="planos">
        <h2 className="text-2xl font-semibold sm:text-3xl">Planos</h2>
        <p className="mb-10 mt-2 text-slate-400">Assinatura mensal, cancele quando quiser. 14 dias de teste grátis.</p>
        <Plans />
      </section>
      <footer className="relative z-10 border-t border-[#1E3A6E]/60 py-10 text-center text-xs text-slate-500">
        <p>PROSPERA+ — Inteligência Financeira & Patrimonial. Conteúdo educacional; não constitui recomendação individual de investimento.</p>
        <p className="mt-2 space-x-4">
          <a href="mailto:rafsp25@gmail.com" className="hover:text-white" data-testid="footer-email">rafsp25@gmail.com</a>
          <a href="tel:+5511996420785" className="hover:text-white" data-testid="footer-phone">(11) 99642-0785</a>
          <a href="https://wa.me/5511996420785" target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300" data-testid="footer-whatsapp">WhatsApp</a>
        </p>
        <p className="mt-2 space-x-4"><Link to="/termos" className="hover:text-white" data-testid="footer-terms">Termos de Uso</Link><Link to="/privacidade" className="hover:text-white" data-testid="footer-privacy">Política de Privacidade</Link></p>
      </footer>
      <a href="https://wa.me/5511996420785?text=Ol%C3%A1!%20Quero%20saber%20mais%20sobre%20o%20PROSPERA%2B" target="_blank" rel="noreferrer" aria-label="Falar no WhatsApp" data-testid="whatsapp-float-btn"
        className="group fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow-2xl transition-transform duration-200 hover:-translate-y-1">
        <MessageCircle className="h-6 w-6" /><span className="hidden sm:inline">Fale no WhatsApp</span>
      </a>
    </div>
  );
}
